export class ApiClientError extends Error {
  code: string;
  status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

interface ErrorPayload {
  error: string;
  message: string;
}

function isErrorPayload(payload: unknown): payload is ErrorPayload {
  if (!payload || typeof payload !== "object") return false;
  const candidate = payload as Record<string, unknown>;
  return typeof candidate.error === "string" && typeof candidate.message === "string";
}

export async function apiRequest<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text) as unknown;
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const fallbackCode = "request_failed";
    const fallbackMessage = "Request failed";
    if (isErrorPayload(payload)) {
      throw new ApiClientError(payload.error, payload.message, response.status);
    }
    throw new ApiClientError(fallbackCode, fallbackMessage, response.status);
  }

  if (isErrorPayload(payload)) {
    throw new ApiClientError(payload.error, payload.message, response.status);
  }

  return payload as T;
}
